import { useState, useEffect } from "react";
import { FiSearch, FiPrinter, FiGrid, FiPlus } from "react-icons/fi";
import { BiSearch } from "react-icons/bi";
import { IoCloseSharp } from "react-icons/io5";
import { Container } from "reactstrap";
import GeneralButton from "../../components/button/GeneralButton";
import FormInput from "../../components/formInput/FormInput";
import AddNewManufacturer from "../../components/modals/AddNewManufacturer";
import DeleteModal from "../../components/modals/DeleteModal";
import ManufacturerTable from "../../components/table/ManufacturerTable";
import useManufacturerHook from "./customHooks/useManufacturerHook";
import TableSkeleton from "../../components/skeleton/TableSkeleton";
const Manufacturers = () => {
    const {
        page,
        setPage,
        pageSize,
        setPageSize,
        totalPages,
        deleteModal,
        handleDelete,
        modal,
        setModal,
        handleSubmitData,
        selectedObj,
        manufacturers,
        search,
        loading,
        setSearch,
        setSelectedMfg,
        setDeleteModal,
        setSelectedObj,
        submitLoading
    } = useManufacturerHook();

    const [searchHistory, setSearchHistory] = useState(() => {
        try {
            const history = localStorage.getItem("mfgSearchHistory");
            if (!history) return [];
            const parsed = JSON.parse(history);
            const now = Date.now();
            return parsed.filter(item => item.timestamp && (now - item.timestamp < 3600000));
        } catch (e) {
            return [];
        }
    });
    const [showHistory, setShowHistory] = useState(false);

    useEffect(() => {
        const interval = setInterval(() => {
            setSearchHistory(prev => {
                const now = Date.now();
                const filtered = prev.filter(item => item.timestamp && (now - item.timestamp < 3600000));
                if (filtered.length !== prev.length) {
                    localStorage.setItem("mfgSearchHistory", JSON.stringify(filtered));
                }
                return filtered;
            });
        }, 60000);
        return () => clearInterval(interval);
    }, []);

    const clearSearch = () => {
        setSearch("");
        localStorage.removeItem("mfgSearch");
        setPage(1);
    };

    const saveToHistory = (term) => {
        const cleanTerm = term?.trim();
        if (!cleanTerm || cleanTerm.length < 2) return;
        setSearchHistory((prev) => {
            const currentHistory = Array.isArray(prev) ? prev : [];
            const now = Date.now();
            const newHistory = [
                { term: cleanTerm, timestamp: now },
                ...currentHistory.filter((item) => item.term !== cleanTerm)
            ].slice(0, 10);
            localStorage.setItem("mfgSearchHistory", JSON.stringify(newHistory));
            return newHistory;
        });
    };

    useEffect(() => {
        if (search && search.trim().length >= 3) {
            const timer = setTimeout(() => {
                saveToHistory(search);
            }, 2000);
            return () => clearTimeout(timer);
        }
    }, [search]);

    const handleHistoryClick = (term) => {
        setSearch(term);
        setPage(1);
        localStorage.setItem("mfgSearch", term);
        setShowHistory(false);
        saveToHistory(term);
    };

    const clearHistory = (e) => {
        e.stopPropagation();
        setSearchHistory([]);
        localStorage.removeItem("mfgSearchHistory");
    };

    return (
        <div className="settings-container">
            <DeleteModal
                toggle={() => setDeleteModal((prev) => !prev)}
                modal={deleteModal}
                handleDelete={handleDelete}
            />
            {modal && (
                <AddNewManufacturer
                    modal={modal}
                    toggle={() => setModal((prev) => !prev)}
                    handleSubmitData={handleSubmitData}
                    selectedObj={selectedObj}
                    loading={submitLoading}
                />
            )}

            <div className="px-4">
                {/* Header & Search Section */}
                <div className="d-flex justify-content-between align-items-center mb-4 search-input-wrapper">
                    <div className="d-flex align-items-center gap-3 flex-1">
                        <div className="bg-primary-soft p-3 rounded-circle shadow-sm">
                            <FiGrid size={24} className="text-primary" />
                        </div>
                        <div>
                            <h4 className="fw-bold text-dark mb-0">Manufacturers</h4>
                            <p className="text-muted small mb-0 mt-1">Management ({totalPages ?? 0} Total)</p>
                        </div>
                    </div>

                    <div className="flex-1 d-flex justify-content-center">
                        <div className="position-relative" style={{ width: "400px" }}>
                            <FormInput
                                type="text"
                                placeholder="Search manufacturer..."
                                size="large"
                                className="border-0 shadow-none"
                                icon={<FiSearch size={20} className="text-muted" />}
                                value={search}
                                handleChange={(e) => {
                                    setPage(1)
                                    setSearch(e.target.value);
                                    localStorage.setItem('mfgSearch', e.target.value)
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        saveToHistory(search);
                                        setShowHistory(false);
                                    }
                                }}
                                onFocus={() => setShowHistory(true)}
                                onBlur={() => setTimeout(() => setShowHistory(false), 200)}
                                suffix={
                                    search ? (
                                        <IoCloseSharp
                                            size={18}
                                            className="text-muted pointer"
                                            onClick={clearSearch}
                                        />
                                    ) : null
                                }
                            />
                            {showHistory && searchHistory.length > 0 && (
                                <div
                                    className="position-absolute w-100 bg-white border shadow-lg mt-1"
                                    style={{
                                        zIndex: 2000,
                                        borderRadius: '4px',
                                        maxHeight: '300px',
                                        overflowY: 'auto',
                                        top: '100%',
                                        left: 0
                                    }}
                                >
                                    <div className="d-flex justify-content-between align-items-center p-2 border-bottom bg-light sticky-top">
                                        <span className="text-muted small fw-bold">RECENT SEARCHES</span>
                                        <span
                                            className="text-primary small cursor-pointer fw-bold"
                                            onClick={clearHistory}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            Clear All
                                        </span>
                                    </div>
                                    {searchHistory.map((item, index) => (
                                        <div
                                            key={index}
                                            className="d-flex align-items-center p-2 border-bottom"
                                            style={{ cursor: 'pointer', transition: 'background 0.2s' }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                            onClick={() => handleHistoryClick(item.term)}
                                        >
                                            <BiSearch size={16} className="text-muted me-2" />
                                            <span className="flex-grow-1" style={{ fontSize: '14px', color: '#333' }}>{item.term}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="d-flex gap-3 flex-1 justify-content-end">
                        <GeneralButton
                            title={"Create Manufacturer"}
                            className={"create-user-btn text-white shadow-sm px-4"}
                            handleClick={() => {
                                setSelectedObj(null);
                                setModal(true);
                            }}
                            icon={<FiPlus className="me-2" />}
                        />
                    </div>
                </div>

                <div className="settings-card">
                    {loading ? (
                        <TableSkeleton columns={8} />
                    ) : (
                        <ManufacturerTable
                            data={manufacturers ?? []}
                            setSelectedObj={setSelectedObj}
                            setModal={setModal}
                            searchValue={search}
                            setSelectedMfg={setSelectedMfg}
                            toggleDelete={() => setDeleteModal((prev) => !prev)}
                            page={page}
                            setPage={setPage}
                            totalPages={totalPages}
                            pageSize={pageSize}
                            setPageSize={setPageSize}
                            onClearSearch={search ? clearSearch : undefined}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default Manufacturers;
